import { startCompletion } from '@codemirror/autocomplete';
import { StreamLanguage } from '@codemirror/language';
import { linter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';

import { EditorLanguageConfiguration } from '../../base-code-editor/configuration/configuration.js';
import type { DQLEditorValidityInfo } from '../dql-editor-types.js';
import { dqlLinter } from './dql-linter.js';
import { dql } from './dql.js';
import { openAutocomplete } from './open-autocomplete.js';
import { suggestionAutocompletion } from './suggestion-autocompletions.js';

const VALIDATION_DELAY = 500;

/**
 * @internal
 * Gets editor configuration for DQL.
 **/
export function getDqlConfig(
  readOnly: boolean,
  onValidationStart?: () => void,
  onValidationEnd?: (validityInfo: DQLEditorValidityInfo[]) => void,
  setValidityInfo?: (validityInfo: DQLEditorValidityInfo[]) => void,
): EditorLanguageConfiguration {
  const language = StreamLanguage.define(dql);

  return {
    language,
    extendWith: [
      language.data.of({
        commentTokens: {
          block: {
            open: '/*',
            close: '*/',
          },
          line: '//',
        },
        closeBrackets: {
          brackets: ['(', '[', '{', '"'],
          before: ')]}:;>|',
          stringPrefixes: [],
        },
      }),
      linter(
        async (view) => {
          const editorContent = view.state.doc.toString();

          const validityResult = await dqlLinter(
            editorContent,
            onValidationStart,
            onValidationEnd,
          );
          setValidityInfo?.(validityResult);
          return validityResult;
        },
        {
          delay: VALIDATION_DELAY,
        },
      ),
      EditorView.updateListener.of(async (update) => {
        const isTyping =
          update.docChanged &&
          update.transactions.some((transaction) =>
            transaction.isUserEvent('input.type'),
          );

        const isDeleting =
          update.docChanged &&
          update.transactions.some((transaction) =>
            transaction.isUserEvent('delete.backward'),
          );

        const selectionChanged =
          update.selectionSet &&
          !update.transactions.some((transaction) =>
            transaction.isUserEvent('input.complete'),
          );

        const isFocused = update.focusChanged && update.view.hasFocus;

        if (!(isTyping || isDeleting || selectionChanged || isFocused)) {
          return;
        }

        if (
          await openAutocomplete(
            {
              isTyping,
              isDeleting,
              isFocused,
              selectionChanged,
            },
            update.state,
          )
        ) {
          startCompletion(update.view);
        }
      }),
    ],
    autocompleteOptions: readOnly
      ? undefined
      : {
          activateOnTyping: false,
          optionClass: (completion) =>
            completion.label.length === 0 ? 'cm-empty-option' : '',
          providers: [suggestionAutocompletion],
        },
  };
}
